import { AnimatePresence, motion, useReducedMotion, useTransform, type Transition } from 'framer-motion';
import { useId, useLayoutEffect, useMemo, useState, type CSSProperties, type FC, type ReactNode } from 'react';

import { BIZY_EYE_CENTER } from './bizy-eye-tracking';
import { useReduceBizyMotionPreference } from './bizy-motion-preferences';
import {
	BIZY3_POSES,
	ALL_BIZY3_STATES as MORPHING_BIZY3_STATES,
	type Bizy3Part,
	type Bizy3Pose,
	type Bizy3Texture,
	type Bizy3State as MorphingBizy3State,
} from './bizy3-poses.generated';
import type { BizyAnimationOverrides } from './types';
import { useBizyEyeTracking } from './use-bizy-eye-tracking';
import { useBizyMascotActive } from './use-bizy-mascot-active';

import './bizy.css';

export const ALL_BIZY3_STATES = MORPHING_BIZY3_STATES;
export type Bizy3State = MorphingBizy3State;

// Shared artboard must fit every export — a few Figma frames overflow 320² (e.g. flyingLeft 322×321, bored 320×324).
const VIEW_BOX = {
	width: Math.max(...Object.values(BIZY3_POSES).map((pose) => pose.viewBox.width)),
	height: Math.max(...Object.values(BIZY3_POSES).map((pose) => pose.viewBox.height)),
} as const;
const ARM_STROKE_WIDTH = 26.0716;
const ANTENNA_STROKE_WIDTH = 7.98851;
const ARM_LAYER_SWITCH_DELAY_SECONDS = 0.2;
const ARM_AMBIENT_START_DELAY_MS = 350;
const BLINK_CYCLE_MS = 8000;
// Blink phase is anchored to a shared epoch so separately mounted eyes (like the
// magnified prop eye) always blink at the same moment as the regular eyes.
const BLINK_EPOCH = Date.now();
// The lens magnifies, so the prop eye looks around slightly more than the regular eyes.
const PROP_EYE_TRACK_SCALE = 1.3;

const COLORS = {
	body: '#FFCC55',
	bodyShade: '#F9A64E',
	eye: '#0E1437',
	outline: 'currentColor',
	purpleWing: '#A08CDE',
	star: '#FBC73A',
	stripe: '#091436',
	squiggle: '#A6ACB5',
	tealWing: '#83D6C9',
} as const;

export type Bizy3Motion = {
	ambientIntensity?: number;
	damping?: number;
	mass?: number;
	stiffness?: number;
};

export type BizyRendererProps = {
	className?: string;
	freeze?: boolean;
	forceMotion?: boolean;
	motion?: Bizy3Motion;
	size?: number | string;
	state?: Bizy3State;
	texture?: boolean;
	animations?: BizyAnimationOverrides;
	armStrokeColor?: string;
	armStrokeOpacity?: number;
	skipEntranceFade?: boolean;
	onBodyClick?: () => void;
	onBodyPointerEnter?: () => void;
	onBodyPointerLeave?: () => void;
};

type EyeGroup = {
	/** Absolute viewBox Y of the visible eye midpoint (paths keep pose Y; only X is group-translated). */
	centerY: number;
	opacities: readonly [number, number];
	parts: readonly [Bizy3Part, Bizy3Part];
	x: number;
};

type Point = {
	x: number;
	y: number;
};

type ArmMotion = {
	calcMode?: 'linear' | 'spline';
	duration: string;
	keySplines?: string;
	keyTimes?: string;
	name: 'excited' | 'headScratch';
	values: string;
};

type MorphPathProps = {
	children?: ReactNode;
	d: string;
	fill?: string;
	filterId?: string;
	/** When false, `d` is static so a SMIL/CSS owner can animate it without fighting framer-motion. */
	morphPath?: boolean;
	opacity?: number;
	stroke?: string;
	strokeLinecap?: 'butt' | 'round' | 'square';
	strokeLinejoin?: 'bevel' | 'miter' | 'round';
	strokeWidth?: number;
	transition: Transition;
};

const normalizeOrthogonalCommands = (path: string): string => {
	const tokens = path.match(/[A-Za-z]|[-+]?(?:\d*\.)?\d+(?:e[-+]?\d+)?/gi);
	if (!tokens) return path;

	const output: string[] = [];
	let command = '';
	let cursor = 0;
	let x = 0;
	let y = 0;

	const readNumber = (): number => Number(tokens[cursor++]);
	const writePoint = (nextX: number, nextY: number): void => {
		x = nextX;
		y = nextY;
		output.push(`${x}`, `${y}`);
	};

	while (cursor < tokens.length) {
		const token = tokens[cursor];
		if (/^[A-Za-z]$/.test(token)) {
			command = token;
			cursor += 1;
		}

		switch (command) {
			case 'M':
			case 'L': {
				output.push(command);
				writePoint(readNumber(), readNumber());
				break;
			}
			case 'C': {
				output.push(command);
				const x1 = readNumber();
				const y1 = readNumber();
				const x2 = readNumber();
				const y2 = readNumber();
				const nextX = readNumber();
				const nextY = readNumber();
				output.push(`${x1}`, `${y1}`, `${x2}`, `${y2}`);
				writePoint(nextX, nextY);
				break;
			}
			case 'H': {
				output.push('L');
				writePoint(readNumber(), y);
				break;
			}
			case 'V': {
				output.push('L');
				writePoint(x, readNumber());
				break;
			}
			case 'Z':
			case 'z': {
				output.push('Z');
				command = '';
				break;
			}
			default:
				return path;
		}
	}

	return output.join(' ');
};

const getPathStart = (path: string): Point => {
	const match = path.match(/^M\s*(-?(?:\d*\.)?\d+)[,\s]+(-?(?:\d*\.)?\d+)/i);
	return match ? { x: Number(match[1]), y: Number(match[2]) } : { x: VIEW_BOX.width / 2, y: VIEW_BOX.height / 2 };
};

const WING_ROOT = { x: 160, y: 165 };

const getClosestPointOnPath = (path: string, target: Point): Point => {
	const numbers = path.match(/[-+]?(?:\d*\.)?\d+(?:e[-+]?\d+)?/gi) ?? [];
	let best = { x: target.x, y: target.y };
	let bestDistance = Number.POSITIVE_INFINITY;

	for (let index = 0; index + 1 < numbers.length; index += 2) {
		const x = Number(numbers[index]);
		const y = Number(numbers[index + 1]);
		if (!Number.isFinite(x) || !Number.isFinite(y)) continue;

		const distance = Math.hypot(x - target.x, y - target.y);
		if (distance < bestDistance) {
			bestDistance = distance;
			best = { x, y };
		}
	}

	return best;
};

// Flutter pivots follow each wing's body attachment so one-sided facing/flying stacks don't spin around center.
const getWingPivot = (path: string): string => {
	const pivot = getClosestPointOnPath(path, WING_ROOT);
	return `${pivot.x.toFixed(1)}px ${pivot.y.toFixed(1)}px`;
};

const squintEyePath = (path: string): string => {
	const numberPattern = /[-+]?(?:\d*\.)?\d+(?:e[-+]?\d+)?/gi;
	const pointCount = Math.floor((path.match(numberPattern) ?? []).length / 2);
	const raisedPointIndexes = new Set([0, 1, pointCount - 2, pointCount - 1]);
	let coordinateIndex = 0;

	return path.replace(numberPattern, (value) => {
		const pointIndex = Math.floor(coordinateIndex / 2);
		const isYCoordinate = coordinateIndex % 2 === 1;
		coordinateIndex += 1;
		if (!isYCoordinate) return `${Number(value)}`;

		const squintOffset = raisedPointIndexes.has(pointIndex) ? -5 : 6;
		return `${Number((Number(value) + squintOffset).toFixed(3))}`;
	});
};

const deformPathFromShoulder = (path: string, handDelta: Point): string => {
	const numberPattern = /[-+]?(?:\d*\.)?\d+(?:e[-+]?\d+)?/gi;
	const coordinateCount = Math.floor((path.match(numberPattern) ?? []).length / 2);
	let coordinateIndex = 0;

	return path.replace(numberPattern, (value) => {
		const pointIndex = Math.floor(coordinateIndex / 2);
		const weight = pointIndex / Math.max(coordinateCount - 1, 1);
		const delta = coordinateIndex % 2 === 0 ? handDelta.x : handDelta.y;
		coordinateIndex += 1;
		return `${Number((Number(value) + delta * weight).toFixed(3))}`;
	});
};

const getArmMotion = (state: MorphingBizy3State, side: 'left' | 'right', path: string): ArmMotion | null => {
	if (state === 'excited') {
		return {
			calcMode: 'linear',
			duration: '1600ms',
			keyTimes: '0;0.25;0.75;1',
			name: 'excited',
			values: [path, deformPathFromShoulder(path, { x: 5, y: 0 }), deformPathFromShoulder(path, { x: -5, y: 0 }), path].join(';'),
		};
	}

	if (side !== 'right') return null;

	if (state === 'headScratch') {
		return {
			duration: '1100ms',
			name: 'headScratch',
			values: [path, deformPathFromShoulder(path, { x: 0, y: -5 }), path, deformPathFromShoulder(path, { x: 1, y: 5 }), path].join(';'),
		};
	}

	return null;
};

// Texture filters are baked into the prop fragment with `_g_` ids; the lens highlight (`_i_` inner shadow) is kept.
const stripPropTextureFilters = (fragment: string): string => fragment.replace(/ filter="url\(#[^"]*filter\d+_g_[^"]*\)"/g, '');

// Prop SVG fragments ship with Figma's global filter ids; prefix them so multiple Bizy3 instances don't collide.
const scopePropFilterIds = (fragment: string, scopeId: string): string =>
	fragment.replace(/\bid="(filter[^"]+)"/g, `id="${scopeId}-$1"`).replace(/url\(#(filter[^)]+)\)/g, `url(#${scopeId}-$1)`);

const isOneSidedWingPose = (state: MorphingBizy3State): boolean =>
	state === 'facingLeft' || state === 'facingRight' || state === 'flyingLeft' || state === 'flyingRight';

const translatePairedPath = (path: string, x: number): string => {
	let coordinateIndex = 0;
	return path.replace(/[-+]?(?:\d*\.)?\d+(?:e[-+]?\d+)?/gi, (value) => {
		const translated = coordinateIndex % 2 === 0 ? Number(value) + x : Number(value);
		coordinateIndex += 1;
		return `${translated}`;
	});
};

const getEyeCenterY = (parts: readonly Bizy3Part[], opacities: readonly [number, number]): number => {
	const visible = parts.filter((_, index) => opacities[index] > 0);
	const sources = visible.length > 0 ? visible : parts;
	const centers = sources.map((part) => part.texture.y + part.texture.height / 2);
	return centers.reduce((sum, y) => sum + y, 0) / centers.length;
};

const getEyeGroup = (state: MorphingBizy3State, pose: Bizy3Pose): EyeGroup => {
	const [firstEye, secondEye] = pose.eyes;
	const [canonicalLeftEye, canonicalRightEye] = BIZY3_POSES.straightOn.eyes;
	const canonicalLeftX = getPathStart(canonicalLeftEye.d).x;
	const canonicalRightX = getPathStart(canonicalRightEye.d).x;

	const normalizeEye = (part: Bizy3Part, canonicalX: number): Bizy3Part => ({
		...part,
		d: translatePairedPath(part.d, canonicalX - getPathStart(part.d).x),
	});

	const withCenterY = (group: Omit<EyeGroup, 'centerY'>): EyeGroup => ({
		...group,
		centerY: getEyeCenterY(group.parts, group.opacities),
	});

	if (state === 'facingRight' && firstEye) {
		return withCenterY({
			opacities: [1, 1],
			parts: [normalizeEye(firstEye, canonicalLeftX), normalizeEye(firstEye, canonicalRightX)],
			x: getPathStart(firstEye.d).x - canonicalLeftX,
		});
	}

	if (state === 'facingLeft' && firstEye) {
		return withCenterY({
			opacities: [1, 1],
			parts: [normalizeEye(firstEye, canonicalLeftX), normalizeEye(firstEye, canonicalRightX)],
			x: getPathStart(firstEye.d).x - canonicalRightX,
		});
	}

	if (state === 'bored' && firstEye) {
		return withCenterY({
			opacities: [1, 0],
			parts: [normalizeEye(firstEye, canonicalLeftX), normalizeEye(firstEye, canonicalRightX)],
			x: getPathStart(firstEye.d).x - canonicalLeftX,
		});
	}

	const leftEye = firstEye ?? canonicalLeftEye;
	const rightEye = secondEye ?? canonicalRightEye;
	const leftOffset = getPathStart(leftEye.d).x - canonicalLeftX;
	const rightOffset = getPathStart(rightEye.d).x - canonicalRightX;

	return withCenterY({
		// In the searching pose the magnifying glass replaces the left eye with its magnified version.
		opacities: state === 'searching' ? [0, 1] : [1, 1],
		parts: [normalizeEye(leftEye, canonicalLeftX), normalizeEye(rightEye, canonicalRightX)],
		x: (leftOffset + rightOffset) / 2,
	});
};

// Aim from the pose-shifted eye midpoint, not the straight-on artboard center.
export const getEyeLookOffset = (viewX: number, viewY: number, eyeGroupX: number, eyeCenterY: number, proximity: number): Point => {
	const x = viewX - (BIZY_EYE_CENTER.x + eyeGroupX);
	const y = viewY - eyeCenterY;
	const magnitude = Math.hypot(x, y);
	if (magnitude === 0) return { x: 0, y: 0 };

	const lookStrength = Math.min(magnitude / 40, 1) * proximity;
	return {
		x: (x / magnitude) * 3.5 * lookStrength,
		y: (y / magnitude) * (y < 0 ? 5.5 : 3.5) * lookStrength,
	};
};

const TextureFilter: FC<{ dilate?: number; id: string; texture: Bizy3Texture }> = ({ dilate = 0, id, texture }) => (
	<filter id={id} x={-80} y={-80} width={480} height={480} filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
		<feFlood floodOpacity="0" result="background" />
		<feBlend in="SourceGraphic" in2="background" mode="normal" result="shape" />
		<feTurbulence
			type="fractalNoise"
			baseFrequency={`${texture.frequency[0]} ${texture.frequency[1]}`}
			numOctaves={texture.numOctaves}
			seed={texture.seed}
			result="noise"
		/>
		<feDisplacementMap in="shape" in2="noise" scale={texture.scale} xChannelSelector="R" yChannelSelector="G" result="displaced" />
		{dilate > 0 && <feMorphology in="displaced" operator="dilate" radius={dilate} result="expanded" />}
		<feMerge>
			<feMergeNode in={dilate > 0 ? 'expanded' : 'displaced'} />
		</feMerge>
	</filter>
);

const MorphPath: FC<MorphPathProps> = ({
	children,
	d,
	fill = 'none',
	filterId,
	morphPath = true,
	opacity = 1,
	stroke,
	strokeLinecap,
	strokeLinejoin,
	strokeWidth,
	transition,
}) => {
	const normalizedPath = normalizeOrthogonalCommands(d);
	return (
		<motion.path
			initial={false}
			d={morphPath ? undefined : normalizedPath}
			animate={morphPath ? { d: normalizedPath, opacity } : { opacity }}
			transition={transition}
			fill={fill}
			filter={filterId ? `url(#${filterId})` : undefined}
			stroke={stroke}
			strokeWidth={strokeWidth}
			strokeLinecap={strokeLinecap}
			strokeLinejoin={strokeLinejoin}
		>
			{children}
		</motion.path>
	);
};

const PartFilter: FC<{ dilate?: number; enabled: boolean; id: string; part: Bizy3Part }> = ({ dilate, enabled, id, part }) =>
	enabled ? <TextureFilter id={id} texture={part.texture} dilate={dilate} /> : null;

// CSS animations start when the class is applied, so separately mounted eyes would blink
// out of phase. Anchoring each element's negative delay to the shared epoch at the moment
// its animation (re)starts puts every eye on the same global blink timeline.
const useBlinkDelay = (blink: boolean): string =>
	// eslint-disable-next-line react-hooks/exhaustive-deps -- recompute only when the blink class is toggled
	useMemo(() => `${-((Date.now() - BLINK_EPOCH) % BLINK_CYCLE_MS)}ms`, [blink]);

const BlinkGroup: FC<{ blink: boolean; children: ReactNode; 'data-bizy3-prop-eye'?: boolean }> = ({ blink, children, ...dataAttributes }) => (
	<g {...dataAttributes} className={blink ? 'bizy3-blink' : undefined} style={{ animationDelay: useBlinkDelay(blink) }}>
		{children}
	</g>
);

export const BizyRenderer: FC<BizyRendererProps> = ({
	className,
	freeze = false,
	forceMotion = false,
	motion: motionOverrides,
	size = 320,
	state = 'curious',
	texture = true,
	animations,
	armStrokeColor,
	armStrokeOpacity,
	skipEntranceFade = false,
	onBodyClick,
	onBodyPointerEnter,
	onBodyPointerLeave,
}) => {
	const id = useId().replaceAll(':', '');
	const prefersReducedMotion = useReducedMotion();
	const [reduceBizyMotion] = useReduceBizyMotionPreference();
	const mascotActive = useBizyMascotActive();
	const isBored = state === 'bored';
	const pose: Bizy3Pose = BIZY3_POSES[state];
	const eyeGroup = getEyeGroup(state, pose);
	const [squintEyeIndex, setSquintEyeIndex] = useState<number | null>(null);
	const reduceMotion = Boolean(prefersReducedMotion || reduceBizyMotion) && !forceMotion;
	const animateAmbientMotion = mascotActive && !freeze && !reduceMotion;
	const animateCharacterAmbientMotion = animateAmbientMotion && !isBored;
	const [ambientArmState, setAmbientArmState] = useState<Bizy3State | null>(null);
	const ambientIntensity = motionOverrides?.ambientIntensity ?? 1;
	const wingAmplitude = animations?.wingAmplitude ?? 1;
	const wingSpeed = animations?.wingSpeed;
	const wingIntensity = ambientIntensity * wingAmplitude;
	const armColor = armStrokeColor ?? COLORS.outline;
	const armOpacity = armStrokeOpacity ?? 1;

	const enableEyeTracking = animateCharacterAmbientMotion && (animations?.eyeTracking ?? true) && !isBored;
	const enablePeekSway = animateCharacterAmbientMotion && state === 'peeking' && (animations?.peekEyeSway ?? true);
	const { eyeOffsetX, eyeOffsetY, svgRef } = useBizyEyeTracking({
		enabled: enableEyeTracking,
		peekIdleSway: enablePeekSway,
		eyeCenterX: BIZY_EYE_CENTER.x + eyeGroup.x,
		eyeCenterY: eyeGroup.centerY,
		viewBox: VIEW_BOX,
	});

	const propEyeOffsetX = useTransform(eyeOffsetX, (value) => value * PROP_EYE_TRACK_SCALE);
	const propEyeOffsetY = useTransform(eyeOffsetY, (value) => value * PROP_EYE_TRACK_SCALE);
	// One-sided stacked wings (facing + flying) fade between families instead of morphing across the body.
	const wingMorphFamily = isOneSidedWingPose(state) ? state : 'morphable';

	const morphTransition = useMemo<Transition>(
		() =>
			reduceMotion
				? { duration: 0 }
				: {
						type: 'spring',
						stiffness: motionOverrides?.stiffness ?? 200,
						damping: motionOverrides?.damping ?? 22,
						mass: motionOverrides?.mass ?? 0.8,
					},
		[motionOverrides?.damping, motionOverrides?.mass, motionOverrides?.stiffness, reduceMotion]
	);
	const eyeTransition = useMemo<Transition>(() => (reduceMotion ? { duration: 0 } : { duration: 0.45, ease: [0.25, 1, 0.5, 1] }), [reduceMotion]);

	const filterId = (part: string): string | undefined => (texture ? `${id}-${part}` : undefined);

	useLayoutEffect(() => {
		setAmbientArmState(null);
		if (!animateCharacterAmbientMotion) return;

		const timer = setTimeout(() => setAmbientArmState(state), ARM_AMBIENT_START_DELAY_MS);
		return () => clearTimeout(timer);
	}, [animateCharacterAmbientMotion, state]);

	const renderArm = (side: 'left' | 'right', frontLayer: boolean): ReactNode => {
		const arm = side === 'left' ? pose.leftArm : pose.rightArm;
		const armMotion = animateCharacterAmbientMotion && ambientArmState === state ? getArmMotion(state, side, arm.d) : null;
		const isWaving = animateCharacterAmbientMotion && ambientArmState === 'waving' && state === 'waving' && side === 'right';
		const armRoot = getPathStart(arm.d);
		const layerOpacity = frontLayer ? (arm.front ? 1 : 0) : 1;
		const combinedOpacity = layerOpacity * armOpacity;

		return (
			<g
				data-bizy3-arm={side}
				data-bizy3-layer={frontLayer ? 'front-overlay' : 'continuous-behind'}
				data-bizy3-render-mode={arm.renderMode}
				mask={frontLayer ? `url(#${id}-body-surface-mask)` : undefined}
				pointerEvents="none"
			>
				<AnimatePresence initial={false}>
					<motion.g
						key={arm.renderMode}
						initial={reduceMotion ? false : { opacity: 0 }}
						animate={{ opacity: 1 }}
						exit={{ opacity: 0 }}
						transition={{ duration: reduceMotion ? 0 : 0.14 }}
					>
						{/* The wave's CSS transform gets its own plain node so Motion only
						    ever owns `opacity` on the parent. Sharing `transform` with
						    Motion lets one clobber the other — on iOS SVG that can leave
						    the class applied while the arm sits still. Same split the
						    ambient bob, antenna sway and wing flutter already use.
						    Rendered unconditionally so toggling the wave never remounts
						    MorphPath and drops its morph continuity. */}
						<g
							data-bizy3-arm-motion={isWaving ? 'waving' : undefined}
							className={isWaving ? 'bizy3-arm-wave' : undefined}
							style={
								isWaving
									? {
											transformBox: 'view-box',
											transformOrigin: `${armRoot.x}px ${armRoot.y}px`,
										}
									: undefined
							}
						>
							<MorphPath
								d={arm.d}
								// SMIL owns `d` during ambient arm loops; MorphPath only springs opacity / pose morphs otherwise.
								morphPath={!armMotion}
								fill={arm.renderMode === 'fill' ? armColor : undefined}
								opacity={combinedOpacity}
								stroke={arm.renderMode === 'stroke' ? armColor : undefined}
								strokeWidth={arm.renderMode === 'stroke' ? ARM_STROKE_WIDTH : undefined}
								strokeLinecap={arm.renderMode === 'stroke' ? 'round' : undefined}
								transition={
									frontLayer
										? {
												...morphTransition,
												opacity: {
													duration: 0,
													delay: reduceMotion || !arm.front ? 0 : ARM_LAYER_SWITCH_DELAY_SECONDS,
												},
											}
										: morphTransition
								}
							>
								{armMotion && (
									<animate
										key={`${state}-${side}`}
										data-bizy3-arm-motion={armMotion.name}
										attributeName="d"
										calcMode={armMotion.calcMode ?? 'spline'}
										dur={armMotion.duration}
										keySplines={
											armMotion.calcMode === 'linear' ? undefined : (armMotion.keySplines ?? '0.42 0 0.58 1;0.42 0 0.58 1;0.42 0 0.58 1;0.42 0 0.58 1')
										}
										keyTimes={armMotion.keyTimes ?? '0;0.25;0.5;0.75;1'}
										repeatCount="indefinite"
										values={armMotion.values}
									/>
								)}
							</MorphPath>
						</g>
					</motion.g>
				</AnimatePresence>
			</g>
		);
	};

	const renderAntenna = (index: 0 | 1, frontLayer: boolean): ReactNode => {
		const antenna = pose.antennae[index];
		const root = getPathStart(antenna.stem.d);
		const direction = index === 0 ? 1 : -1;
		return (
			<motion.g
				data-bizy3-antenna={index}
				initial={false}
				animate={{ opacity: antenna.front === frontLayer ? 1 : 0 }}
				transition={{ duration: reduceMotion ? 0 : 0.1 }}
			>
				<g
					className={animateCharacterAmbientMotion && (animations?.antennaSway ?? true) ? 'bizy3-antenna-sway' : undefined}
					style={
						{
							'--bizy3-antenna-delay': index === 0 ? '0ms' : '180ms',
							'--bizy3-antenna-duration': index === 0 ? '2.8s' : '3.2s',
							'--bizy3-antenna-from': `${-3.2 * direction * ambientIntensity}deg`,
							'--bizy3-antenna-skew-from': `${-1.2 * direction * ambientIntensity}deg`,
							'--bizy3-antenna-skew-to': `${1.1 * direction * ambientIntensity}deg`,
							'--bizy3-antenna-to': `${2.8 * direction * ambientIntensity}deg`,
							transformBox: 'view-box',
							transformOrigin: `${root.x}px ${root.y}px`,
						} as CSSProperties
					}
				>
					<MorphPath d={antenna.stem.d} stroke={COLORS.outline} strokeWidth={ANTENNA_STROKE_WIDTH} strokeLinecap="round" transition={morphTransition} />
					{/* Outline, not eye: the tip hangs off the body against the page, so
					    it follows the theme like the stem it sits on. The eye colour is
					    for details painted on the yellow, which never meet the backdrop. */}
					<MorphPath d={antenna.tip.d} fill={COLORS.outline} transition={morphTransition} />
				</g>
			</motion.g>
		);
	};

	const wingStyle = (direction: 1 | -1, transformOrigin: string): CSSProperties =>
		({
			'--bizy3-wing-narrow': 1 - 0.004 * wingIntensity,
			'--bizy3-wing-rotation-in': `${-0.3 * direction * wingIntensity}deg`,
			'--bizy3-wing-rotation-out': `${0.65 * direction * wingIntensity}deg`,
			'--bizy3-wing-short': 1 - 0.05 * wingIntensity,
			'--bizy3-wing-tall': 1 + 0.0125 * wingIntensity,
			'--bizy3-wing-wide': 1 + 0.0125 * wingIntensity,
			transformBox: 'view-box',
			transformOrigin,
			...(wingSpeed !== undefined ? { animationDuration: `${wingSpeed}s` } : null),
		}) as CSSProperties;

	const enableHoverBob = animateCharacterAmbientMotion && (animations?.hoverBob ?? true);
	const ambientMotionClassName = enableHoverBob ? (state === 'tickleLeft' ? 'bizy3-tickle' : 'bizy3-bob') : undefined;
	const enableWingFlutter = animateCharacterAmbientMotion && wingAmplitude !== 0;
	const enableBlink = animateCharacterAmbientMotion && (animations?.blink ?? true);

	return (
		<motion.svg
			ref={svgRef}
			data-bizy3-bored={isBored || undefined}
			data-bizy3-state={state}
			className={className}
			width={size}
			height={size}
			viewBox={`0 0 ${VIEW_BOX.width} ${VIEW_BOX.height}`}
			fill="none"
			xmlns="http://www.w3.org/2000/svg"
			aria-hidden
			initial={skipEntranceFade || reduceMotion ? false : { opacity: 0 }}
			animate={{ opacity: 1 }}
			transition={{ duration: skipEntranceFade || reduceMotion ? 0 : 0.25 }}
			style={{ display: 'block', overflow: 'visible' }}
		>
			<defs>
				<PartFilter enabled={texture} id={`${id}-body`} part={pose.body} />
				<PartFilter enabled={texture} id={`${id}-body-surface-mask-texture`} part={pose.body} dilate={2} />
				<PartFilter enabled={texture} id={`${id}-body-shade`} part={pose.bodyShade} />
				<mask id={`${id}-body-mask`} maskUnits="userSpaceOnUse" x={-80} y={-80} width={480} height={480}>
					<MorphPath d={pose.bodyMask} fill="white" stroke="white" strokeWidth={3.1954} transition={morphTransition} />
				</mask>
				<mask id={`${id}-body-surface-mask`} maskUnits="userSpaceOnUse" x={-80} y={-80} width={480} height={480}>
					<MorphPath
						d={pose.body.d}
						fill="white"
						filterId={filterId('body-surface-mask-texture')}
						stroke="white"
						strokeWidth={3.1954}
						transition={morphTransition}
					/>
				</mask>
			</defs>

			<g
				data-bizy3-ambient-root
				className={ambientMotionClassName}
				style={
					{
						'--bizy3-bob-down': `${3 * ambientIntensity}px`,
						'--bizy3-bob-rotation': `${-0.3 * ambientIntensity}deg`,
						'--bizy3-bob-rotation-opposite': `${0.3 * ambientIntensity}deg`,
						'--bizy3-bob-up': `${-5 * ambientIntensity}px`,
						transformBox: 'view-box',
						transformOrigin: '160px 160px',
					} as CSSProperties
				}
			>
				<AnimatePresence initial={false}>
					<motion.g
						key={wingMorphFamily}
						data-bizy3-wing-family={wingMorphFamily}
						initial={reduceMotion ? false : { opacity: 0 }}
						animate={{ opacity: 1 }}
						exit={{ opacity: 0 }}
						transition={{ duration: reduceMotion ? 0 : 0.16, ease: [0.25, 1, 0.5, 1] }}
					>
						{/* Paint order matches Figma: both teals under both purples. */}
						<g
							data-bizy3-wing="teal-right"
							className={enableWingFlutter ? 'bizy3-wing-flutter bizy3-wing-flutter-a' : undefined}
							style={wingStyle(1, getWingPivot(pose.wings.tealRight.d))}
						>
							<MorphPath d={pose.wings.tealRight.d} fill={COLORS.tealWing} opacity={0.6} transition={morphTransition} />
						</g>
						<g
							data-bizy3-wing="teal-left"
							className={enableWingFlutter ? 'bizy3-wing-flutter bizy3-wing-flutter-a' : undefined}
							style={wingStyle(-1, getWingPivot(pose.wings.tealLeft.d))}
						>
							<MorphPath d={pose.wings.tealLeft.d} fill={COLORS.tealWing} opacity={0.6} transition={morphTransition} />
						</g>
						<g
							data-bizy3-wing="purple-right"
							className={enableWingFlutter ? 'bizy3-wing-flutter bizy3-wing-flutter-b' : undefined}
							style={wingStyle(1, getWingPivot(pose.wings.purpleRight.d))}
						>
							<MorphPath d={pose.wings.purpleRight.d} fill={COLORS.purpleWing} opacity={0.6} transition={morphTransition} />
						</g>
						<g
							data-bizy3-wing="purple-left"
							className={enableWingFlutter ? 'bizy3-wing-flutter bizy3-wing-flutter-b' : undefined}
							style={wingStyle(-1, getWingPivot(pose.wings.purpleLeft.d))}
						>
							<MorphPath d={pose.wings.purpleLeft.d} fill={COLORS.purpleWing} opacity={0.6} transition={morphTransition} />
						</g>
					</motion.g>
				</AnimatePresence>

				{renderArm('left', false)}
				{renderArm('right', false)}
				{renderAntenna(0, false)}
				{renderAntenna(1, false)}

				<g
					data-bizy3-body-hit-area
					onClick={onBodyClick}
					onPointerEnter={onBodyPointerEnter}
					onPointerLeave={onBodyPointerLeave}
					style={onBodyClick ? { cursor: 'pointer' } : undefined}
				>
					<MorphPath d={pose.body.d} fill={COLORS.body} filterId={filterId('body')} stroke={COLORS.body} strokeWidth={3.1954} transition={morphTransition} />

					<g data-bizy3-body-content mask={`url(#${id}-body-mask)`}>
						<MorphPath
							d={pose.bodyShade.d}
							filterId={filterId('body-shade')}
							stroke={COLORS.bodyShade}
							strokeWidth={53.523}
							strokeLinecap="round"
							strokeLinejoin="round"
							transition={morphTransition}
						/>
						{pose.stripes.map((stripe, index) => (
							<MorphPath key={`stripe-${index}`} d={stripe.d} fill={COLORS.stripe} transition={morphTransition} />
						))}
						<motion.g data-bizy3-eye-group initial={false} animate={{ x: eyeGroup.x }} transition={morphTransition}>
							<motion.g style={{ x: eyeOffsetX, y: eyeOffsetY }}>
								<BlinkGroup blink={enableBlink}>
									{eyeGroup.parts.map((eye, index) => (
										<g
											key={`eye-${index}`}
											data-bizy3-eye={index}
											data-bizy3-squinting={squintEyeIndex === index || undefined}
											className="bizy3-eye-squint-target"
											pointerEvents={eyeGroup.opacities[index] === 0 ? 'none' : undefined}
											onPointerEnter={() => setSquintEyeIndex(index)}
											onPointerLeave={() => setSquintEyeIndex((currentIndex) => (currentIndex === index ? null : currentIndex))}
										>
											<MorphPath
												d={squintEyeIndex === index && state !== 'excited' ? squintEyePath(eye.d) : eye.d}
												fill={COLORS.eye}
												opacity={eyeGroup.opacities[index]}
												stroke={COLORS.eye}
												strokeWidth={0.798851}
												transition={eyeTransition}
											/>
										</g>
									))}
								</BlinkGroup>
							</motion.g>
						</motion.g>
					</g>
				</g>

				{renderAntenna(0, true)}
				{renderAntenna(1, true)}
				<g data-bizy3-stinger>
					<MorphPath d={pose.stinger.d} fill={COLORS.outline} transition={morphTransition} />
				</g>
				{renderArm('left', true)}
				{renderArm('right', true)}

				{pose.stars.map((star, index) => (
					<g
						key={`${state}-star-${index}`}
						className={animateCharacterAmbientMotion ? 'bizy3-star-enter' : undefined}
						style={{ '--bizy3-star-delay': `${index * 150}ms` } as CSSProperties}
					>
						<path
							data-bizy3-star={index}
							className={animateCharacterAmbientMotion ? 'bizy3-star-twinkle' : undefined}
							d={star.d}
							fill={COLORS.star}
							style={{ '--bizy3-star-delay': `${index * 150}ms` } as CSSProperties}
						/>
					</g>
				))}
				<AnimatePresence initial={false}>
					{pose.prop && (
						<motion.g
							key={`${state}-prop`}
							data-bizy3-prop
							initial={reduceMotion ? false : { opacity: 0 }}
							animate={{ opacity: 1 }}
							exit={{ opacity: 0 }}
							transition={{ duration: reduceMotion ? 0 : 0.25, ease: 'easeOut' }}
						>
							<g
								// eslint-disable-next-line react/no-danger -- prop fragments are generated at build time from our own SVG exports
								dangerouslySetInnerHTML={{
									__html: scopePropFilterIds(stripPropTextureFilters(pose.prop.behindEye), id),
								}}
							/>
							{pose.prop.eye && (
								<motion.g style={{ x: propEyeOffsetX, y: propEyeOffsetY }}>
									<BlinkGroup blink={enableBlink} data-bizy3-prop-eye>
										<MorphPath d={pose.prop.eye.d} fill={COLORS.eye} stroke={COLORS.eye} strokeWidth={1.48467} transition={eyeTransition} />
									</BlinkGroup>
								</motion.g>
							)}
							{pose.prop.inFrontOfEye && (
								<g
									// eslint-disable-next-line react/no-danger -- prop fragments are generated at build time from our own SVG exports
									dangerouslySetInnerHTML={{
										__html: scopePropFilterIds(stripPropTextureFilters(pose.prop.inFrontOfEye), id),
									}}
								/>
							)}
						</motion.g>
					)}
				</AnimatePresence>
				{pose.squiggles.map((squiggle, index) => (
					<path
						key={`${state}-squiggle-${index}`}
						data-bizy3-bored-squiggle={index}
						className={animateAmbientMotion ? 'bizy3-bored-squiggle' : undefined}
						d={squiggle.d}
						fill="none"
						stroke={COLORS.squiggle}
						strokeWidth={3}
						strokeLinecap="round"
						pathLength={1}
					/>
				))}
			</g>
		</motion.svg>
	);
};
